import { createContext, useContext, useEffect, useState, ReactNode, useMemo } from "react";

type Theme = "light" | "dark" | "colorful" | "yellow" | "pink" | "orange" | "blue" | "red" | "green";

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const allowedThemes: Theme[] = ["light", "dark", "colorful", "yellow", "pink", "orange", "blue", "red", "green"];

export function ThemeProvider({ children }: Readonly<{ children: ReactNode }>) {
  const [theme, setThemeState] = useState<Theme>(() => {
    // Initialize from localStorage if available (client-side only)
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("theme") as Theme | null;
      if (saved && allowedThemes.includes(saved)) {
        return saved;
      }
      return "dark";
    }
    return "dark";
  });

  const [isInitialized, setIsInitialized] = useState(false);

  // Apply theme to document on mount and when theme changes
  useEffect(() => {
    const root = document.documentElement;
    console.log("🎨 Applying theme:", theme);
    root.classList.remove("light", "dark", "colorful", "yellow", "pink", "orange", "blue", "red", "green");
    root.classList.add(theme);
    localStorage.setItem("theme", theme);
  }, [theme]);

  // Initialize theme from user profile when auth context is available
  useEffect(() => {
    if (!isInitialized && typeof window !== "undefined") {
      // Check if user info exists in auth context
      const authData = localStorage.getItem("userInfo");
      if (authData) {
        try {
          const userInfo = JSON.parse(authData);
          if (userInfo.selectedTheme) {
            setThemeState(userInfo.selectedTheme as Theme);
          }
        } catch (e) {
          console.error("Failed to parse user info for theme:", e);
        }
      }
      setIsInitialized(true);
    }
  }, [isInitialized]);

  const toggleTheme = () => {
    console.log("🔄 Toggle theme called");
    setThemeState((prev) => {
      if (prev === "dark") return "light";
      if (prev === "light") return "colorful";
      return "dark";
    });
  };

  const setTheme = (newTheme: Theme) => {
    console.log("✨ Set theme called with:", newTheme);
    setThemeState(newTheme);
  };

  const contextValue = useMemo(() => ({ theme, toggleTheme, setTheme }), [theme]);

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}
